/**
 * Business rules for the user registration flow, derived from the SFDC
 * "/api/sfdc/check-user" response, shaped like:
 * { is_registered, totalSize, contact: { ..., Titan_Portal_Status__c } }.
 *
 * Scenarios:
 * 1. totalSize > 0 and Titan_Portal_Status__c === 'Active'         -> allow chatbot access.
 * 2. totalSize > 0 and Titan_Portal_Status__c === 'Not Registered' -> allow chatbot access.
 * 3. totalSize === 0 (no matching SFDC record)                     -> redirect to registration.
 *
 * In short: any matching SFDC record (totalSize > 0) grants chatbot access
 * regardless of Titan_Portal_Status__c; only a missing record blocks access.
 */

/**
 * Determine whether the chatbot should be accessible based on the SFDC
 * check-user response.
 * @param {object} sfdcResponse - Response from the /api/sfdc/check-user endpoint.
 * @returns {boolean} True if the user should be allowed to use the chatbot.
 */
export function isChatbotAccessAllowed(sfdcResponse) {
  const totalSize = Number(sfdcResponse?.totalSize) || 0;

  // No matching SFDC record — user must complete registration first (scenario 3).
  if (totalSize <= 0) {
    return false;
  }

  // A matching record exists — allow access regardless of Titan_Portal_Status__c
  // (covers both 'Active' and 'Not Registered', per scenarios 1 and 2).
  return true;
}

/**
 * Extract the Titan Portal status from the SFDC contact record, if present.
 * @param {object} sfdcResponse - Response from the /api/sfdc/check-user endpoint.
 * @returns {string|null} The Titan_Portal_Status__c value, or null if absent.
 */
export function getTitanPortalStatus(sfdcResponse) {
  return sfdcResponse?.contact?.Titan_Portal_Status__c ?? null;
}
